import { DateTime } from 'luxon';
import { getDynamicPositionAttributes } from '~/modules/common/charts/timeline/calculations';
import {
  mapIsoStringtoUtcObject,
  mapRepliconDateToUtcObject
} from '~/modules/common/dates/convert';
import { useMeContext } from '~/modules/me/useMeContext';
import { getPeriodsByScale } from '~/modules/resourcing/common/hooks/usePeriodsByScale';
import { getTotalHoursForPeriodFromScheduleRules } from '~/modules/resourcing/common/util/scheduleUtil';
import { isNumeric } from '~/modules/common/numbers';
import { ResourceCostModeType } from '~/types';
import {
  getTotalScheduledHoursForPeriod,
  getScheduleRulesInChartDisplayRange
} from './useAllocationTimelineBlocks2';

const useOverlayTimelineBlocks = ({
  chartDisplayDateRange,
  allocation,
  scale,
  showTotalCost = false,
  skip
}) => {
  const me = useMeContext();

  if (skip) return { overlayPeriods: [] };

  const {
    startDate: chartStartDate,
    endDate: chartEndDate
  } = chartDisplayDateRange;
  const { resourceCostMode } = me;
  const isRoleCostMode = resourceCostMode === ResourceCostModeType.Rolecost;

  const { scheduleRules, user, startDate, endDate } = allocation;

  const allocationStartDate = mapIsoStringtoUtcObject(startDate);
  const allocationEndDate = mapIsoStringtoUtcObject(endDate);

  const noOverlap =
    allocationEndDate < chartStartDate || allocationStartDate > chartEndDate;

  const scheduleRulesInRange = noOverlap
    ? []
    : getScheduleRulesInChartDisplayRange({
        rangeStart: chartStartDate,
        rangeEnd: chartEndDate,
        scheduleRules
      });

  const { displayPeriods } = getPeriodsByScale({
    me,
    rangeStart: chartStartDate,
    rangeEnd: chartEndDate,
    scale
  });

  const scheduleHoursByDate = (user?.scheduleDurationByDay || []).map(d => ({
    date: mapRepliconDateToUtcObject(d.date),
    hours: d.hours
  }));

  return {
    overlayPeriods: displayPeriods.map(range => {
      const { start, end, key } = range;

      const allocatedHoursForRange = getTotalHoursForPeriodFromScheduleRules({
        start,
        end,
        scheduleRules: scheduleRulesInRange
      });

      const noPeriodOverlap =
        allocationEndDate < start || allocationStartDate > end;

      const scheduledHoursForRange = getTotalScheduledHoursForPeriod({
        start: noPeriodOverlap
          ? start
          : DateTime.max(allocationStartDate, start),
        end: noPeriodOverlap ? end : DateTime.min(allocationEndDate, end),
        scheduleHoursByDate
      });

      return {
        key,
        startDate: start,
        endDate: end,
        dynamicPosition: getDynamicPositionAttributes({
          chartStartDate,
          start,
          end,
          scale
        }).dynamicPosition,
        totalHours: allocatedHoursForRange,
        percentage: scheduledHoursForRange
          ? (allocatedHoursForRange / scheduledHoursForRange) * 100
          : undefined,
        totalScheduleHours: scheduledHoursForRange,
        totalCost:
          showTotalCost && user && isNumeric(allocatedHoursForRange)
            ? {
                amount:
                  allocatedHoursForRange *
                    (isRoleCostMode
                      ? user.primaryRoleCostRate
                      : user.costRate) || 0,
                symbol: isRoleCostMode
                  ? user.primaryRoleCostCurrency &&
                    user.primaryRoleCostCurrency.displayText
                  : user.costCurrency && user.costCurrency.displayText
              }
            : undefined
      };
    })
  };
};

export default useOverlayTimelineBlocks;
